import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { markMarketingVisitor } from '../../utils/launchContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { LanguageSwitcher } from './LanguageSwitcher';

export function MarketingHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  const NAV_LINKS = [
    { to: '/welcome', label: t('nav.home'), key: 'home' },
    { to: '/about', label: t('nav.about'), key: 'about' },
    { to: '/contact', label: t('nav.contact'), key: 'contact' },
    { to: '/support', label: t('nav.support'), key: 'support' },
  ];

  useEffect(() => {
    markMarketingVisitor();
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const handleLogin = () => {
    markMarketingVisitor();
    navigate('/login');
  };
  const handleRegister = () => {
    markMarketingVisitor();
    navigate('/register');
  };

  return (
    <header
      data-testid="marketing-header"
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 60,
        backdropFilter: 'blur(14px)',
        WebkitBackdropFilter: 'blur(14px)',
        background: scrolled ? 'var(--mkt-header-bg-scrolled)' : 'var(--mkt-header-bg)',
        borderBottom: scrolled ? '1px solid var(--mkt-header-border)' : '1px solid transparent',
        transition: 'background-color 220ms ease, border-color 220ms ease',
      }}
    >
      <div
        style={{
          maxWidth: 1180,
          margin: '0 auto',
          padding: '14px 22px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 14,
        }}
      >
        <Link
          to="/welcome"
          data-testid="marketing-header-logo-link"
          onClick={markMarketingVisitor}
          style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}
        >
          <img src="/allure-logo.png" alt="Allure" style={{ width: 36, height: 36, objectFit: 'contain' }} />
          <span style={{ fontFamily: "'Inria Serif', serif", fontSize: 22, fontWeight: 700, color: 'var(--mkt-text)', letterSpacing: '-0.01em' }}>
            Allure
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="marketing-nav-desktop" style={{ display: 'flex', alignItems: 'center', gap: 28 }}>
          {NAV_LINKS.map((link) => {
            const isActive = location.pathname === link.to;
            return (
              <Link
                key={link.to}
                to={link.to}
                onClick={markMarketingVisitor}
                data-testid={`marketing-nav-${link.key}`}
                style={{
                  fontFamily: "'Manrope', sans-serif",
                  fontSize: 14,
                  fontWeight: 600,
                  color: isActive ? 'var(--mkt-text)' : 'var(--mkt-text-muted)',
                  textDecoration: 'none',
                  position: 'relative',
                  padding: '6px 0',
                }}
              >
                {link.label}
                {isActive ? (
                  <span style={{ position: 'absolute', left: 0, right: 0, bottom: -2, height: 2, background: '#E53935', borderRadius: 2 }} />
                ) : null}
              </Link>
            );
          })}
        </nav>

        <div className="marketing-cta-desktop" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <LanguageSwitcher dataTestPrefix="marketing-header" />
          <button
            type="button"
            onClick={handleLogin}
            data-testid="marketing-header-login-button"
            style={{
              fontFamily: "'Manrope', sans-serif",
              fontSize: 14,
              fontWeight: 600,
              color: 'var(--mkt-text)',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '8px 14px',
            }}
          >
            {t('nav.login')}
          </button>
          <button
            type="button"
            onClick={handleRegister}
            data-testid="marketing-header-register-button"
            style={{
              fontFamily: "'Manrope', sans-serif",
              fontSize: 14,
              fontWeight: 700,
              color: 'var(--mkt-cta-solid-text)',
              background: 'var(--mkt-cta-solid-bg)',
              border: 'none',
              borderRadius: 9999,
              cursor: 'pointer',
              padding: '10px 18px',
              boxShadow: '0 6px 16px rgba(22,17,39,0.18)',
            }}
          >
            {t('nav.get_started')}
          </button>
        </div>

        <div className="marketing-menu-mobile-cluster" style={{ display: 'none', alignItems: 'center', gap: 8 }}>
          <LanguageSwitcher dataTestPrefix="marketing-header-mobile" />
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? 'Close menu' : 'Open menu'}
            data-testid="marketing-header-menu-button"
            style={{
              background: 'transparent',
              border: '1px solid var(--mkt-header-border)',
              borderRadius: 12,
              padding: 8,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {open ? <X size={20} color="var(--mkt-text)" /> : <Menu size={20} color="var(--mkt-text)" />}
          </button>
        </div>
      </div>

      {open ? (
        <div
          data-testid="marketing-mobile-menu"
          style={{
            borderTop: '1px solid var(--mkt-header-border)',
            padding: '18px 22px 22px',
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
            background: 'var(--mkt-page-bg)',
          }}
        >
          {NAV_LINKS.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              onClick={markMarketingVisitor}
              data-testid={`marketing-mobile-nav-${link.key}`}
              style={{
                fontFamily: "'Manrope', sans-serif",
                fontSize: 16,
                fontWeight: 600,
                color: 'var(--mkt-text)',
                textDecoration: 'none',
                padding: '8px 0',
              }}
            >
              {link.label}
            </Link>
          ))}
          <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
            <button
              type="button"
              onClick={handleLogin}
              data-testid="marketing-mobile-login-button"
              style={{
                flex: 1,
                fontFamily: "'Manrope', sans-serif",
                fontSize: 14,
                fontWeight: 700,
                color: 'var(--mkt-text)',
                background: 'transparent',
                border: '1px solid var(--mkt-text)',
                borderRadius: 9999,
                padding: '12px 18px',
                cursor: 'pointer',
              }}
            >
              {t('nav.login')}
            </button>
            <button
              type="button"
              onClick={handleRegister}
              data-testid="marketing-mobile-register-button"
              style={{
                flex: 1,
                fontFamily: "'Manrope', sans-serif",
                fontSize: 14,
                fontWeight: 700,
                color: 'var(--mkt-cta-solid-text)',
                background: 'var(--mkt-cta-solid-bg)',
                border: 'none',
                borderRadius: 9999,
                padding: '12px 18px',
                cursor: 'pointer',
              }}
            >
              {t('nav.get_started')}
            </button>
          </div>
        </div>
      ) : null}

      <style>{`
        @media (max-width: 760px) {
          .marketing-nav-desktop { display: none !important; }
          .marketing-cta-desktop { display: none !important; }
          .marketing-menu-mobile-cluster { display: inline-flex !important; }
        }
      `}</style>
    </header>
  );
}
